import { useEffect, useRef, useCallback } from "react";
import { useTheme } from "@/contexts/ThemeContext";

const MESH_COLS = 40;
const MESH_ROWS = 20;
const MESH_COLS_MOBILE = 20;
const MESH_ROWS_MOBILE = 10;
const FOCAL_LENGTH = 800;

const WAVE = {
  A1: 40, freq1: 0.02, speed1: 0.0008,
  A2: 25, freq2: 0.03, speed2: 0.0012,
  A3: 15, freq3: 0.015, speed3: 0.0005,
};

const DARK_COLORS = {
  baseBg: "#0A0A1A",
  meshRgb: "147,130,220",
  meshOpacityScale: 1,
  g1Inner: [88, 60, 160],
  g1Alpha: 0.4,
  g2Inner: [180, 120, 40],
  g2Alpha: 0.25,
  g1Fade: "rgba(15,10,30,0)",
  g2Fade: "rgba(15,10,30,0)",
  nodeColor: [120, 200, 255],
  nodeAlpha: 0.6,
  nodeGlowAlpha: 0.4,
  nodeBlur: 15,
  sparkColor: [255, 180, 60],
  sparkAlpha: 0.5,
  sparkGlowAlpha: 0.3,
  sparkBlur: 10,
  diamondColor: [255, 255, 255],
  diamondAlpha: 0.7,
};

const LIGHT_COLORS = {
  baseBg: "#F8F9FC",
  meshRgb: "0,180,190",
  meshOpacityScale: 0.5,
  g1Inner: [0, 200, 210],
  g1Alpha: 0.08,
  g2Inner: [0, 150, 255],
  g2Alpha: 0.06,
  g1Fade: "rgba(248,249,252,0)",
  g2Fade: "rgba(248,249,252,0)",
  nodeColor: [0, 180, 190],
  nodeAlpha: 0.25,
  nodeGlowAlpha: 0.15,
  nodeBlur: 8,
  sparkColor: [0, 130, 255],
  sparkAlpha: 0.2,
  sparkGlowAlpha: 0.1,
  sparkBlur: 6,
  diamondColor: [0, 180, 190],
  diamondAlpha: 0.35,
};

function createMesh(cols, rows, offsetX, offsetY, zOffset, w, h) {
  const verts = [];
  const spacingX = (w * 0.8) / cols;
  const spacingZ = (h * 0.7) / rows;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      verts.push({
        baseX: offsetX + c * spacingX,
        baseY: offsetY,
        baseZ: zOffset + r * spacingZ,
        x: 0, y: 0, z: 0, px: 0, py: 0,
      });
    }
  }
  return verts;
}

function createParticles(count, w, h, isMobile) {
  const particles = [];
  const nodeCount = isMobile ? 8 : Math.floor(count * 0.55);
  const sparkCount = isMobile ? 5 : Math.floor(count * 0.33);
  const diamondCount = isMobile ? 0 : count - nodeCount - sparkCount;

  for (let i = 0; i < nodeCount; i++) {
    particles.push({
      type: "node", x: Math.random() * w, y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.6, vy: (Math.random() - 0.5) * 0.4,
      radius: 2 + Math.random() * 2, phase: Math.random() * Math.PI * 2,
    });
  }
  for (let i = 0; i < sparkCount; i++) {
    particles.push({
      type: "spark", x: w * 0.5 + Math.random() * w * 0.5, y: Math.random() * h,
      vx: (Math.random() - 0.5) * 1.0, vy: (Math.random() - 0.5) * 0.6,
      radius: 1 + Math.random(), phase: Math.random() * Math.PI * 2,
    });
  }
  for (let i = 0; i < diamondCount; i++) {
    particles.push({
      type: "diamond", x: w * 0.4 + Math.random() * w * 0.5, y: Math.random() * h * 0.5,
      vx: (Math.random() - 0.5) * 0.2, vy: (Math.random() - 0.5) * 0.15,
      radius: 3 + Math.random() * 2, phase: Math.random() * Math.PI * 2,
    });
  }
  return particles;
}

function project(vx, vy, vz, cx, cy) {
  const scale = FOCAL_LENGTH / (FOCAL_LENGTH + vz);
  return { x: (vx - cx) * scale + cx, y: (vy - cy) * scale + cy };
}

export default function QuantumBackground({ intensity = 1.0 }) {
  const { isDark } = useTheme();
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef(null);

  const init = useCallback((canvas, dark) => {
    const isMobile = window.innerWidth < 768;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const parent = canvas.parentElement;
    const w = parent ? parent.clientWidth : window.innerWidth;
    const h = parent ? parent.clientHeight : window.innerHeight;

    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + "px";
    canvas.style.height = h + "px";

    const ctx = canvas.getContext("2d", { willReadFrequently: false });
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cols = isMobile ? MESH_COLS_MOBILE : MESH_COLS;
    const rows = isMobile ? MESH_ROWS_MOBILE : MESH_ROWS;
    const mesh1 = createMesh(cols, rows, -w * 0.1, h * 0.4, 0, w, h);
    const mesh2 = createMesh(cols, rows, w * 0.2, h * 0.35, 200, w, h);
    const particles = createParticles(isMobile ? 15 : 30, w, h, isMobile);
    const C = dark ? DARK_COLORS : LIGHT_COLORS;

    // Cache gradients
    const diag = Math.sqrt(w * w + h * h);
    const g1 = ctx.createRadialGradient(w * 0.3, h * 0.4, 0, w * 0.3, h * 0.4, diag * 0.5);
    g1.addColorStop(0, `rgba(${C.g1Inner[0]},${C.g1Inner[1]},${C.g1Inner[2]},${C.g1Alpha * intensity})`);
    g1.addColorStop(1, C.g1Fade);

    const g2 = ctx.createRadialGradient(w * 0.75, h * 0.55, 0, w * 0.75, h * 0.55, diag * 0.4);
    g2.addColorStop(0, `rgba(${C.g2Inner[0]},${C.g2Inner[1]},${C.g2Inner[2]},${C.g2Alpha * intensity})`);
    g2.addColorStop(1, C.g2Fade);

    stateRef.current = { ctx, w, h, dpr, cols, rows, mesh1, mesh2, particles, g1, g2, isMobile, intensity, C };
  }, [intensity]);

  const animate = useCallback((time) => {
    const s = stateRef.current;
    if (!s) return;
    const { ctx, w, h, cols, rows, mesh1, mesh2, particles, g1, g2, C } = s;

    ctx.fillStyle = C.baseBg;
    ctx.fillRect(0, 0, w, h);

    // Gradient fog with slow drift
    ctx.save();
    ctx.translate(Math.cos(time * 0.0001) * 30, Math.sin(time * 0.00012) * 20);
    ctx.fillStyle = g1;
    ctx.fillRect(-40, -40, w + 80, h + 80);
    ctx.restore();

    ctx.save();
    ctx.translate(Math.sin(time * 0.00015) * 20, Math.cos(time * 0.00013) * 15);
    ctx.fillStyle = g2;
    ctx.fillRect(-40, -40, w + 80, h + 80);
    ctx.restore();

    const cx = w / 2;
    const cy = h / 2;
    const maxZ = 500;

    const drawMesh = (mesh, zOff) => {
      for (let i = 0; i < mesh.length; i++) {
        const v = mesh[i];
        const x = v.baseX;
        const z = v.baseZ;
        v.y = v.baseY
          + WAVE.A1 * Math.sin(x * WAVE.freq1 + time * WAVE.speed1)
          + WAVE.A2 * Math.sin(z * WAVE.freq2 + time * WAVE.speed2)
          + WAVE.A3 * Math.sin((x + z) * WAVE.freq3 + time * WAVE.speed3);
        v.x = x;
        v.z = z + zOff;
        const p = project(v.x, v.y, v.z, cx, cy);
        v.px = p.x;
        v.py = p.y;
      }

      const avgDepth = 1 - (zOff + maxZ * 0.3) / maxZ;
      const opacity = (0.15 + 0.15 * Math.max(0, Math.min(1, avgDepth))) * s.intensity * C.meshOpacityScale;
      ctx.strokeStyle = `rgba(${C.meshRgb},${opacity})`;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const idx = r * cols + c;
          const v = mesh[idx];
          if (c < cols - 1) { const next = mesh[idx + 1]; ctx.moveTo(v.px, v.py); ctx.lineTo(next.px, next.py); }
          if (r < rows - 1) { const below = mesh[idx + cols]; ctx.moveTo(v.px, v.py); ctx.lineTo(below.px, below.py); }
        }
      }
      ctx.stroke();
    };

    drawMesh(mesh2, 200);
    drawMesh(mesh1, 0);

    // Particles
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];
      p.x += p.vx;
      p.y += p.vy;
      if (p.x < -10) p.x = w + 10;
      if (p.x > w + 10) p.x = -10;
      if (p.y < -10) p.y = h + 10;
      if (p.y > h + 10) p.y = -10;

      ctx.save();
      if (p.type === "node") {
        ctx.shadowBlur = C.nodeBlur;
        ctx.shadowColor = `rgba(${C.nodeColor},${C.nodeGlowAlpha * s.intensity})`;
        ctx.fillStyle = `rgba(${C.nodeColor},${C.nodeAlpha * s.intensity})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.type === "spark") {
        const pulse = 1 + 0.6 * Math.sin(time * 0.002 + p.phase);
        ctx.shadowBlur = C.sparkBlur;
        ctx.shadowColor = `rgba(${C.sparkColor},${C.sparkGlowAlpha * s.intensity})`;
        ctx.fillStyle = `rgba(${C.sparkColor},${C.sparkAlpha * s.intensity})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius * pulse, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.type === "diamond") {
        const twinkle = 0.2 + 0.6 * (0.5 + 0.5 * Math.sin(time * 0.0015 + p.phase));
        ctx.fillStyle = `rgba(${C.diamondColor},${twinkle * C.diamondAlpha * s.intensity})`;
        ctx.translate(p.x, p.y);
        ctx.rotate(Math.PI / 4);
        ctx.fillRect(-p.radius / 2, -p.radius / 2, p.radius, p.radius);
      }
      ctx.restore();
    }

    rafRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    init(canvas, isDark);

    if (prefersReduced) {
      animate(0);
      return;
    }

    const onVisibility = () => {
      if (document.hidden) {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      } else {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => init(canvas, isDark), 200);
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("resize", onResize);
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("resize", onResize);
      clearTimeout(resizeTimer);
    };
  }, [init, animate, isDark]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ zIndex: 0, willChange: "transform" }}
      data-testid="quantum-background"
    />
  );
}
